import React, { useState } from "react";

import { updateUserAttributes } from "aws-amplify/auth";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button, Text } from "@ui/elements";
import { Checkbox } from "@ui/forms";
import { Stack } from "@ui/layout";

import { toaster, useAuth } from "@components";

type EmailPreferencesFields = {
  platformUpdates: boolean;
  marketingEmails: boolean;
  newsLetters: boolean;
  feedbackSurvey: boolean;
};

export const EmailPreferences = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const authContext = useAuth();

  const { handleSubmit, register } = useForm<EmailPreferencesFields>({
    defaultValues: {
      platformUpdates:
        authContext.userAttributes?.["custom:platformUpdates"] === "true",
      marketingEmails:
        authContext.userAttributes?.["custom:marketingEmails"] === "true",
      newsLetters:
        authContext.userAttributes?.["custom:newsLetters"] === "true",
      feedbackSurvey:
        authContext.userAttributes?.["custom:feedbackSurvey"] === "true",
    },
  });

  const onSubmit = async (values: EmailPreferencesFields) => {
    setLoading(true);
    setError("");
    try {
      await updateUserAttributes({
        userAttributes: {
          "custom:platformUpdates": String(values.platformUpdates),
          "custom:marketingEmails": String(values.marketingEmails),
          "custom:newsLetters": String(values.newsLetters),
          "custom:feedbackSurvey": String(values.feedbackSurvey),
        },
      });

      toaster.success({
        title: t("messages.successful-update"),
        message: t("messages.update-email-preferences-success"),
      });
    } catch (error) {
      setLoading(false);
      if (error instanceof Error) {
        return setError(t(error.message));
      }

      setError(String(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack direction="column" gap="xl">
        {error && (
          <div className="notification is-danger is-light">{error}</div>
        )}
        <Stack direction="column" gap="xs">
          <Text variant="secondary">
            {t("account-settings.email-preferences-info-1")}
          </Text>
          <Text variant="secondary">
            {t("account-settings.email-preferences-info-2")}
          </Text>
        </Stack>

        <Stack direction="column">
          <Checkbox isCentered={false} {...register("platformUpdates")}>
            <Text weight="bold">{t("email-preferences.platform-updates")}</Text>
            <Text variant="secondary">
              {t("email-preferences.platform-updates-description")}
            </Text>
          </Checkbox>
          <Checkbox isCentered={false} {...register("feedbackSurvey")}>
            <Text weight="bold">
              {t("email-preferences.feedback-and-surveys")}
            </Text>
            <Text variant="secondary">
              {t("email-preferences.feedback-and-surveys-description")}
            </Text>
          </Checkbox>
          <Checkbox isCentered={false} {...register("marketingEmails")}>
            <Text weight="bold">{t("email-preferences.marketing")}</Text>
            <Text variant="secondary">
              {t("email-preferences.marketing-description")}
            </Text>
          </Checkbox>
          <Checkbox isCentered={false} {...register("newsLetters")}>
            <Text weight="bold">{t("email-preferences.newsletters")}</Text>
            <Text variant="secondary">
              {t("email-preferences.newsletters-description")}
            </Text>
          </Checkbox>
        </Stack>

        <Stack justify="end">
          <Button variant="light" isLoading={loading}>
            {t("labels.update")}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};
